import { defineContract, nullableString, timestamp, z } from "./_base";

/*
 * Contacts (the API calls them "entities").
 *
 * Verified against apps/api/src/routes/entities.ts and the Entity /
 * EntityIdentifier / EntityScore models in packages/db/prisma/schema.prisma.
 * As everywhere in this layer, only the fields this app renders are declared.
 */

export const identifierSchema = z.object({
  id: z.string(),
  idType: z.string(),          /* phone | email | linkedin | ...            */
  idValue: z.string(),
  label: nullableString,
  isPrimary: z.boolean().optional().default(false),
});
export type Identifier = z.infer<typeof identifierSchema>;

export const scoresSchema = z.object({
  /* The design's "RSI" column is this total. */
  totalScore: z.number().optional().default(0),
  influence: z.number().optional().default(0),
  relationshipStrength: z.number().optional().default(0),
  strategicValue: z.number().optional().default(0),
  risk: z.number().optional().default(0),
  reconnectEase: z.number().optional().default(0),
  lastScoredAt: timestamp,
});
export type Scores = z.infer<typeof scoresSchema>;

export const contactSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  givenName: nullableString,
  familyName: nullableString,
  avatarUrl: nullableString,
  category: nullableString,
  tier: nullableString,
  notes: nullableString,
  organization: nullableString,
  title: nullableString,
  location: nullableString,
  industry: nullableString,
  linkedinUrl: nullableString,
  relationshipStage: nullableString,
  howWeMet: nullableString,
  tags: z.array(z.string()).nullish().transform((v) => v ?? []),
  lastInteractionAt: timestamp,
  createdAt: timestamp,
  identifiers: z.array(identifierSchema).nullish().transform((v) => v ?? []),
  /* One-to-one; absent until the scorer has run for this contact. */
  scores: scoresSchema.nullish(),
  /* Added by the list handler from whatsapp_message; null when that table is
   * empty or missing, which the handler swallows deliberately. */
  lastWhatsApp: timestamp,
});
export type Contact = z.infer<typeof contactSchema>;

/** Primary phone, else first phone. The design's PHONE column. */
export function primaryPhone(c: Contact): string | undefined {
  const phones = c.identifiers.filter((i) => i.idType === "phone");
  return (phones.find((p) => p.isPrimary) ?? phones[0])?.idValue;
}

export function primaryEmail(c: Contact): string | undefined {
  const emails = c.identifiers.filter((i) => i.idType === "email");
  return (emails.find((p) => p.isPrimary) ?? emails[0])?.idValue;
}

/** Whole days since the last interaction — the design's SILENT column. */
export function silentDays(c: Contact): number | undefined {
  const last = c.lastInteractionAt ?? c.lastWhatsApp;
  if (!last) return undefined;
  return Math.max(0, Math.floor((Date.now() - last.getTime()) / 86_400_000));
}

/* ── List ─────────────────────────────────────────────────────────────────
 * Filters are exactly what the querystring supports — search, page, limit,
 * tier, category, sortBy, sortOrder, hasPhone, filter=stale. Deliberately no
 * client-side filtering: with server pagination it would filter only the page
 * in hand and silently under-report.
 */
export interface ContactsQuery {
  search?: string;
  page?: number;
  limit?: number;
  tier?: string;
  category?: string;
  sortBy?: "displayName" | "lastInteractionAt" | "totalScore" | "createdAt";
  sortOrder?: "asc" | "desc";
  hasPhone?: boolean;
  stale?: boolean;
}

export const contactsContract = defineContract({
  name: "entities.list",
  method: "GET",
  path: () => "/entities",
  response: z.object({
    data: z.array(contactSchema).optional().default([]),
    total: z.number().optional().default(0),
    page: z.number().optional().default(1),
    limit: z.number().optional().default(50),
    totalPages: z.number().optional().default(1),
  }),
  params: (q: ContactsQuery) => ({
    ...(q.search ? { search: q.search } : {}),
    page: String(q.page ?? 1),
    limit: String(q.limit ?? 50),
    ...(q.tier ? { tier: q.tier } : {}),
    ...(q.category ? { category: q.category } : {}),
    sortBy: q.sortBy ?? "displayName",
    sortOrder: q.sortOrder ?? "asc",
    ...(q.hasPhone ? { hasPhone: "true" } : {}),
    ...(q.stale ? { filter: "stale" } : {}),
  }),
});

/* ── Stats ────────────────────────────────────────────────────────────────
 * Drives the category and tier filters with real values and counts rather
 * than a hardcoded list that would drift from the data.
 */
export const contactStatsContract = defineContract({
  name: "entities.stats",
  method: "GET",
  path: () => "/entities/stats",
  response: z.object({
    total: z.number().optional().default(0),
    byCategory: z.record(z.number()).optional().default({}),
    byTier: z.record(z.number()).optional().default({}),
    profiled: z.number().optional().default(0),
    relationships: z.number().optional().default(0),
    messages: z.number().optional().default(0),
    whatsappConnected: z.boolean().optional().default(false),
  }),
});

/* ── Detail ───────────────────────────────────────────────────────────────
 * GET /entities/:id returns the entity plus identifiers, scores, profile,
 * relationships both directions, observations, list memberships, a history
 * table read by raw SQL, and a computed engagement block.
 */

const relatedEntitySchema = z.object({
  id: z.string(),
  displayName: z.string(),
  entityType: nullableString,
  avatarUrl: nullableString,
});

export const relationshipSchema = z.object({
  id: z.string(),
  relationshipType: z.string(),
  strength: z.number().optional().default(1),
  /* 0..1. Below 1 with confirmedByUser false means the AI proposed it and
   * nobody has agreed yet — the design renders that as "Colleague? 70%". */
  confidence: z.number().optional().default(1),
  source: nullableString,
  confirmedByUser: z.boolean().optional().default(false),
  toEntity: relatedEntitySchema.nullish(),
  fromEntity: relatedEntitySchema.nullish(),
});
export type Relationship = z.infer<typeof relationshipSchema>;

export const observationSchema = z.object({
  id: z.string(),
  provider: z.string(),
  externalObjectType: z.string(),
  observedAt: timestamp,
  /* Free-form per provider; read defensively at the render site. */
  payload: z.record(z.unknown()).nullish().transform((v) => v ?? {}),
});
export type Observation = z.infer<typeof observationSchema>;

export const historyItemSchema = z.object({
  id: z.string(),
  category: nullableString,
  title: nullableString,
  subtitle: nullableString,
  startYear: z.union([z.number(), z.string(), z.null()]).optional(),
  endYear: z.union([z.number(), z.string(), z.null()]).optional(),
  location: nullableString,
  details: nullableString,
});
export type HistoryItem = z.infer<typeof historyItemSchema>;

export const listMemberSchema = z.object({
  list: z.object({
    id: z.string(),
    name: z.string(),
    listType: nullableString,
  }).nullish(),
});

export const engagementSchema = z.object({
  messagesSynced: z.number().optional().default(0),
  inbound: z.number().optional().default(0),
  outbound: z.number().optional().default(0),
  lastSeenAt: timestamp,
  lastEngagedAt: timestamp,
  /* "them" means the ball is in your court. */
  lastReplyBy: z.union([z.literal("them"), z.literal("me"), z.null()]).optional(),
  noise: z.union([z.number(), z.null()]).optional(),
  signal: z.union([z.number(), z.null()]).optional(),
});

export const profileSchema = z.object({
  summary: nullableString,
  strategicValue: nullableString,
  riskAssessment: nullableString,
  engagementAdvice: nullableString,
  iceBreaker: nullableString,
  lastProfiledAt: timestamp,
});

export const contactDetailSchema = contactSchema.extend({
  profile: profileSchema.nullish(),
  relationshipsFrom: z.array(relationshipSchema).nullish().transform((v) => v ?? []),
  relationshipsTo: z.array(relationshipSchema).nullish().transform((v) => v ?? []),
  observations: z.array(observationSchema).nullish().transform((v) => v ?? []),
  listMembers: z.array(listMemberSchema).nullish().transform((v) => v ?? []),
  history: z.array(historyItemSchema).nullish().transform((v) => v ?? []),
  engagement: engagementSchema.nullish(),
});
export type ContactDetail = z.infer<typeof contactDetailSchema>;

/** Both directions flattened to "the other person + how they relate". */
export function relationsOf(c: ContactDetail): Array<{
  id: string; other: { id: string; displayName: string } | undefined;
  type: string; confidence: number; confirmed: boolean;
}> {
  return [
    ...c.relationshipsFrom.map((r) => ({ r, other: r.toEntity })),
    ...c.relationshipsTo.map((r) => ({ r, other: r.fromEntity })),
  ]
    .filter((x) => x.other)
    .map(({ r, other }) => ({
      id: r.id,
      other: other ? { id: other.id, displayName: other.displayName } : undefined,
      type: r.relationshipType,
      confidence: r.confidence,
      confirmed: r.confirmedByUser,
    }));
}

export const contactDetailContract = defineContract({
  name: "entities.detail",
  method: "GET",
  path: (input: { id: string }) => `/entities/${input.id}`,
  response: contactDetailSchema,
});

/* ── Engagement breakdown ─────────────────────────────────────────────────
 * GET /whatsapp/engagement/:entityId
 *
 * The contact detail's own `engagement` block gives totals. This gives the
 * six-factor breakdown the relationship score is actually made of, computed
 * from the message store on each request.
 *
 * It answers `{ available: false }` — a 200, not a 404 — when the contact has
 * no synced messages, so the union below is the real contract, and "no data"
 * is a state to render rather than an error to throw.
 */
const engagementFactorSchema = z.object({
  score: z.number().optional().default(0),
  value: z.string().optional().default(""),
  label: z.string().optional().default(""),
});
export type EngagementFactor = z.infer<typeof engagementFactorSchema>;

/*
 * A single object rather than a discriminated union: the "no messages" reply
 * is `{ available: false }` with every other key absent, and defaults model
 * that exactly. A union technically described it better but did not narrow
 * reliably at the call site, and an unreadable type is worse than a simple one.
 */
export const engagementDetailSchema = z.object({
  available: z.boolean().optional().default(false),
  engagementIndex: z.number().optional().default(0),
  scores: z.object({
    frequency: engagementFactorSchema.optional(),
    recency: engagementFactorSchema.optional(),
    reciprocity: engagementFactorSchema.optional(),
    responsiveness: engagementFactorSchema.optional(),
    depth: engagementFactorSchema.optional(),
    initiative: engagementFactorSchema.optional(),
  }).optional().default({}),
  meta: z.record(z.unknown()).optional().default({}),
});
export type EngagementDetail = z.infer<typeof engagementDetailSchema>;

export const engagementDetailContract = defineContract({
  name: "whatsapp.engagement",
  method: "GET",
  path: (input: { entityId: string }) => `/whatsapp/engagement/${input.entityId}`,
  response: engagementDetailSchema,
});

/* ── Writes ───────────────────────────────────────────────────────────────
 * Verified against routes/entities.ts: POST / (create), PATCH /:id (whitelist
 * below — phone/email/CNIC are NEVER changeable through it), POST
 * /:id/identifiers, POST /:id/history, DELETE /:id/identifiers/:identId.
 */
export const EDITABLE_FIELDS = [
  "displayName", "givenName", "familyName", "salutation",
  "category", "tier", "tags", "notes",
  "rank", "organization", "title", "industry", "location", "university",
  "cohortYear", "unit", "nationality", "linkedinUrl",
  "sharedHistory", "gender", "profession", "subUnit",
] as const;
export type EditableField = (typeof EDITABLE_FIELDS)[number];

export const createContactContract = defineContract({
  name: "entities.create",
  method: "POST",
  path: () => "/entities",
  response: z.object({ id: z.string(), displayName: z.string() }).passthrough(),
  params: (input: { displayName: string; category?: string; tier?: string; notes?: string; organization?: string; title?: string; tags?: string[] }) => input,
});

export const updateContactContract = defineContract({
  name: "entities.update",
  method: "PATCH",
  path: (input: { id: string; updates: Partial<Record<EditableField, unknown>> }) => `/entities/${input.id}`,
  response: z.unknown(),
  params: (input) => input.updates,
});

export const addIdentifierContract = defineContract({
  name: "entities.identifiers.add",
  method: "POST",
  path: (input: { id: string; idType: string; idValue: string; label?: string; isPrimary?: boolean }) => `/entities/${input.id}/identifiers`,
  response: z.unknown(),
  params: ({ idType, idValue, label, isPrimary }) => ({ idType, idValue, ...(label ? { label } : {}), ...(isPrimary ? { isPrimary } : {}) }),
});

export const removeIdentifierContract = defineContract({
  name: "entities.identifiers.remove",
  method: "DELETE",
  path: (input: { id: string; identId: string }) => `/entities/${input.id}/identifiers/${input.identId}`,
  response: z.unknown(),
});

export const addHistoryContract = defineContract({
  name: "entities.history.add",
  method: "POST",
  path: (input: { id: string; category: string; title: string; subtitle?: string; startYear?: number; endYear?: number; location?: string; details?: string }) => `/entities/${input.id}/history`,
  response: z.unknown(),
  params: ({ id: _id, ...body }) => body,
});

/* ── Contact page extras (Figma 50:65): journal, circles, calendar, links ── */

/** Category → dot colour, lifted from the Figma contacts-list rows (66:227).
 *  Unknown categories fall back to a stable hash so nothing is ever uncoloured. */
export const CATEGORY_COLORS: Record<string, string> = {
  Military: "#CF7A6E", Academic: "#8BA4E8", Personal: "#7C837E",
  Family: "#6FC493", Professional: "#6D9BE8", Service: "#5FB3AD", Business: "#D9A441",
};
export function categoryColor(category: string | null | undefined): string {
  if (!category) return "var(--k-text-muted)";
  if (CATEGORY_COLORS[category]) return CATEGORY_COLORS[category]!;
  const hues = ["#CF7A6E", "#8BA4E8", "#7C837E", "#6FC493", "#6D9BE8", "#5FB3AD", "#D9A441"];
  let h = 0; for (let i = 0; i < category.length; i++) h = (h * 31 + category.charCodeAt(i)) >>> 0;
  return hues[h % hues.length]!;
}

export const journalEntrySchema = z.object({
  id: z.string(), content: z.string().optional().default(""), entryType: z.string().optional().default("note"),
  sentiment: nullableString, tags: z.array(z.string()).nullish().transform((v) => v ?? []), createdAt: timestamp,
});
export type JournalEntry = z.infer<typeof journalEntrySchema>;

export const journalContract = defineContract({
  name: "journal.list", method: "GET", path: (i: { entityId: string }) => `/journal/${i.entityId}`,
  // Real payload is a bare array (verified 08-21); older builds wrapped it as { entries } — accept both, expose { entries }.
  response: z.union([z.array(journalEntrySchema), z.object({ entries: z.array(journalEntrySchema).optional().default([]) })])
    .transform((v) => (Array.isArray(v) ? { entries: v } : v)),
  params: (_i) => ({ limit: "100" }),
});
export const addJournalContract = defineContract({
  // API mounts journal at /journal with POST /:entityId — the entity id is in the
  // PATH, and only {content, entryType, sentiment, tags} go in the body. Posting
  // to /journal with entityId in the body 404s ("Route POST:/journal not found").
  name: "journal.add", method: "POST",
  path: (i: { entityId: string }) => `/journal/${i.entityId}`,
  response: z.unknown(),
  params: (i: { entityId: string; content: string; entryType?: string; tags?: string[] }) => ({
    content: i.content, entryType: i.entryType, tags: i.tags,
  }),
});

export const circleSchema = z.object({
  id: z.string(), name: z.string(), circleType: nullableString,
  members: z.array(z.object({ id: z.string(), displayName: z.string().optional() })).nullish().transform((v) => v ?? []),
  _count: z.object({ members: z.number().optional() }).nullish(),
});
export type Circle = z.infer<typeof circleSchema>;
export const circlesContract = defineContract({
  name: "circles.list", method: "GET", path: () => "/circles",
  response: z.object({ circles: z.array(circleSchema).optional().default([]) }),
});

export const calendarEventSchema = z.object({
  id: z.string(), title: nullableString, startAt: timestamp, endAt: timestamp, status: nullableString, location: nullableString,
}).passthrough();
export const calendarByEntityContract = defineContract({
  name: "calendar.byEntity", method: "GET", path: () => "/calendar",
  response: z.object({ data: z.array(calendarEventSchema).optional().default([]) }),
  params: (i: { entityId: string }) => ({ entityId: i.entityId, limit: "50" }),
});

export const confirmRelationshipContract = defineContract({
  name: "relationships.confirm", method: "POST", path: (i: { id: string }) => `/relationships/confirm/${i.id}`, response: z.unknown(),
});
export const rejectRelationshipContract = defineContract({
  name: "relationships.reject", method: "POST", path: (i: { id: string }) => `/relationships/reject/${i.id}`, response: z.unknown(),
});

/* WhatsApp: the contact's chat, then its messages (for the timeline quotes).
 * /whatsapp/chats carries entity_id per chat. */
export const waChatSchema = z.object({
  chatId: z.string(), name: nullableString, type: nullableString,
  messages: z.number().optional(), inbound: z.number().optional(), outbound: z.number().optional(),
  lastAt: timestamp,
  /* The linked contact, when the chat has been matched to one. */
  contact: z.object({ id: z.string().optional(), displayName: nullableString }).nullish(),
}).passthrough();
export const waChatsContract = defineContract({
  name: "whatsapp.chats", method: "GET", path: () => "/whatsapp/chats",
  response: z.union([z.object({ chats: z.array(waChatSchema) }), z.array(waChatSchema)]).transform((v) => (Array.isArray(v) ? v : v.chats)),
  /* Search by the contact's name rather than paging the whole chat list: there
   * are 400+ chats and the endpoint supports `search`. The caller still matches
   * on contact.id — the name only narrows the candidates. */
  /* Without a search the caller wants the whole set (coverage, rhythm): the
   * API defaults to a small page, so ask for 2000. With a search, 25 is plenty. */
  params: (i: { search?: string; limit?: number }) => ({ limit: String(i.limit ?? (i.search ? 25 : 2000)), ...(i.search ? { search: i.search } : {}) }),
});
export const waMessageSchema = z.object({
  id: z.string().optional(), body: nullableString, text: nullableString, direction: nullableString, from: nullableString,
  timestamp: timestamp, type: nullableString, media: z.unknown().optional(),
}).passthrough();
export const waMessagesContract = defineContract({
  name: "whatsapp.messages", method: "GET", path: (i: { chatId: string }) => `/whatsapp/chats/${encodeURIComponent(i.chatId)}/messages`,
  response: z.union([z.object({ messages: z.array(waMessageSchema) }), z.array(waMessageSchema)]).transform((v) => (Array.isArray(v) ? v : v.messages)),
  params: (_i) => ({ limit: "30" }),
});
